
import { defineComponent, reactive, ref, toRef, toRefs, provide } from "vue";
import { usePicker, PickerType, Picker, UfUploadFileType, useDate } from "@/composables";
import { userService, hqdbService } from "@/service";
import { Toast } from "vant";
import { useRouter } from "vue-router";
import { RouterName } from "@/router";
import validate from "@/utils/validate";
import HouseTree, { HouseTreeType } from "./house/HouseTree.vue";
const getDefaultForm = () => ({
  name: "",
  mobile: "",
  id_card: ""
});
interface HouseItem {
  place: string;
  id: string;
}
interface HouseData {
  house_id: HouseItem;
  house_user_type: Picker<"Single">;
  house_user_state: string;
}
interface DataProps {
  formData: ReturnType<typeof getDefaultForm>;
  houseData: Array<HouseData>;
  idcardData: UfUploadFileType[];
  daterangePickerIsShow: boolean;
  daterangePickerData: Date[];
}
interface ErrorInfo<T> {
  values: T;
  errors: {
    name: string;
    message: string;
  }[];
}

export default defineComponent({
  name: "ResidenceAuth",
  components: { HouseTree },
  setup() {
    provide("multiple", false);

    const router = useRouter();
    const { picker: genderPicker } = usePicker(PickerType.Single.GENDER);
    const { picker: houseTypePickker } = usePicker(PickerType.Single.HOUSE_TYPE);
    const { daterangePickerDateFormat } = useDate();

    const house = reactive<DataProps>({
      formData: getDefaultForm(),
      idcardData: [],
      houseData: [],
      daterangePickerIsShow: false,
      daterangePickerData: []
    });
    const addHouse = () => {
      const { picker } = usePicker(PickerType.Single.HOUSE_TYPE);
      house.houseData.push({
        house_id: { id: "", place: "" },
        house_user_type: picker,
        house_user_state: "1"
      });
    };
    const removeHouse = (index: number) => house.houseData.splice(index, 1);

    const validityOfIDCard = daterangePickerDateFormat(toRef(house, "daterangePickerData"));

    const onSubmit = async (values: DataProps["formData"]) => {
      const house_json = house.houseData.map(el => ({
        house_id: el.house_id.id,
        house_user_state: el.house_user_state,
        house_user_type: el.house_user_type.value
      }));

      try {
        await userService.authentication({
          type: "8",
          name: values.name,
          mobile: values.mobile,
          gender: genderPicker.value,
          positive: house.idcardData[0]?.res.id,
          back: house.idcardData[1]?.res.id,
          id_card: values.id_card,
          start_time: validityOfIDCard.value.startTimestamp,
          end_time: validityOfIDCard.value.endTimestamp,
          house_json: JSON.stringify(house_json)
        });
        await router.push({
          name: RouterName.AUTHENTICATION_DETAIL
        });
      } catch (error) {}
    };

    const onFailed = (errorInfo: ErrorInfo<DataProps["formData"]>) => {
      Toast.fail(errorInfo.errors[0].message);
    };

    const houseTreeRef = ref<HouseTreeType>();
    const chooseHouse = reactive({
      show: false,
      searchName: "",
      currentIndex: 0,
      list: [],
      getList: async () => {
        const { data } = await hqdbService.subHouse({ search_name: chooseHouse.searchName });
        chooseHouse.list = data.data;
      },
      confirm() {
        const info = houseTreeRef.value?.getCurrentNode() as unknown;
        if (info) {
          const { place, id } = info as HouseItem;
          house.houseData[chooseHouse.currentIndex].house_id = { place, id };
          chooseHouse.show = false;
        }
      }
    });

    const handleHouse = (index: number) => {
      chooseHouse.show = true;
      chooseHouse.currentIndex = index;
      houseTreeRef.value?.updateChecked(house.houseData[index].house_id);
    };

    addHouse();
    chooseHouse.getList();

    return {
      handleHouse,
      ...toRefs(house),
      houseTreeRef,
      onSubmit,
      onFailed,
      genderPicker,
      houseTypePickker,
      addHouse,
      removeHouse,
      validityOfIDCard,
      validate,
      chooseHouse
    };
  }
});
